import { useCallback } from "react";
import type { BaseSyntheticEvent } from "react";
import type { FieldErrors, FieldValues } from "react-hook-form";

interface Options {
  /**
   * 明示スコープ。指定があれば最優先で使う。
   * 通常は handleSubmit から渡される event.target (= submit を受けた form) を
   * 自動でスコープにするので未指定で OK。
   */
  scope?: HTMLElement | null;
  /** スクロール後にフォーカスも当てるか (default: true) */
  focus?: boolean;
}

/**
 * RHF errors ツリーをすべて走査し、leaf error の field path を配列で返す。
 * ネスト ("private_detail.area") / 配列 ("items.0.price") の両方に対応。
 */
const collectErrorPaths = (errors: unknown, prefix = ""): string[] => {
  if (!errors || typeof errors !== "object") return [];
  const paths: string[] = [];

  for (const key of Object.keys(errors as Record<string, unknown>)) {
    const value = (errors as Record<string, unknown>)[key];
    if (!value || typeof value !== "object") continue;

    const v = value as Record<string, unknown>;
    const hasLeafShape = "type" in v || "message" in v;
    const childKeys = Object.keys(v).filter(
      (k) => k !== "type" && k !== "message" && k !== "ref" && k !== "types",
    );
    const fullPath = prefix ? `${prefix}.${key}` : key;

    if (hasLeafShape && childKeys.length === 0) {
      paths.push(fullPath);
      continue;
    }
    const child = collectErrorPaths(value, fullPath);
    if (child.length > 0) {
      paths.push(...child);
    } else if (hasLeafShape) {
      paths.push(fullPath);
    }
  }
  return paths;
};

/**
 * field path に対応する DOM 要素を、優先度順のセレクタ群で探す。
 *
 * 優先度:
 *   1. [data-rhf-field="..."]  — 共有コンポーネント側で明示的に付与した場合
 *   2. [name="..."]:not([type=hidden])  — CustomTextInput など可視 input
 *   3. [data-testid="select-..."]  — CustomSingleSelect / Multi の visible な Select 要素
 *   4. [name="..."]  — 上記で漏れた hidden input (Select の内部 input 等)
 *   5. #id  — 最後の保険
 */
const findFieldElement = (
  name: string,
  scope: ParentNode,
): HTMLElement | null => {
  const escaped =
    typeof CSS !== "undefined" && CSS.escape ? CSS.escape(name) : name;
  const selectors = [
    `[data-rhf-field="${escaped}"]`,
    `[name="${escaped}"]:not([type="hidden"])`,
    `[data-testid="select-${escaped}"]`,
    `[name="${escaped}"]`,
    `#${escaped}`,
  ];
  for (const sel of selectors) {
    try {
      const el = scope.querySelector<HTMLElement>(sel);
      if (el) return el;
    } catch {
      // 不正なセレクタはスキップ
    }
  }
  return null;
};

/**
 * バリデーションエラー時に、エラーフィールドのうち画面上で最上位にあるものへ
 * scrollIntoView (block: "center") するハンドラを返す。
 *
 * 使い方:
 * ```tsx
 * const scrollToFirstError = useScrollToFirstError();
 * <form onSubmit={handleSubmit(onValid, scrollToFirstError)} />
 * ```
 *
 * 設計上のポイント:
 * - エラーが複数ある場合は「DOM 上で最上位のフィールド」を選ぶ。RHF の errors キー順序
 *   (register/Controller の mount 順) に依存せず、ユーザの目線に合う。
 *   DOM が見つからない path は自動的にスキップ。
 * - DOM 検索は submit を受けた `<form>` 配下に絞る。CustomDrawer は close でも DOM に
 *   居続けるため、document 全体検索だと別 form の同名 input を誤検出する。
 * - 着地位置は `block: "center"` で viewport の縦中央。先頭フィールドが対象の場合は
 *   スクロール上限が 0 でクランプされるので自然に最上端に止まる。
 *
 * 注意: 共有コンポーネント側で `name` prop を必ず指定すること。
 * (CustomTextInput / CustomSingleSelect / CustomCalendarInput 等は対応済み)
 */
export const useScrollToFirstError = <T extends FieldValues = FieldValues>({
  scope,
  focus = true,
}: Options = {}) => {
  return useCallback(
    (errors: FieldErrors<T>, event?: BaseSyntheticEvent) => {
      const paths = collectErrorPaths(errors);
      if (paths.length === 0) return;

      const eventTarget = event?.target as HTMLElement | undefined;
      const formScope: HTMLElement | null =
        scope ??
        (eventTarget instanceof HTMLFormElement
          ? eventTarget
          : eventTarget?.closest?.("form") ?? null);

      const root: ParentNode = formScope ?? document;

      // 各 error path の DOM を解決し、viewport 座標で最上位のものを選ぶ。
      let topMostTarget: HTMLElement | null = null;
      let topMostTop = Number.POSITIVE_INFINITY;
      for (const p of paths) {
        const el = findFieldElement(p, root);
        if (!el) continue;
        const visible =
          el.offsetParent !== null
            ? el
            : (el.closest<HTMLElement>(
                ".MuiFormControl-root, [data-rhf-field], [data-field-row]",
              ) ?? el);
        const rect = visible.getBoundingClientRect();
        if (rect.top < topMostTop) {
          topMostTop = rect.top;
          topMostTarget = visible;
        }
      }

      if (!topMostTarget) return;

      topMostTarget.scrollIntoView({ behavior: "smooth", block: "center" });

      if (focus) {
        // ターゲット自身が focusable な要素 (input/textarea/select 等) の場合のみ focus する。
        // wrapper Box (data-rhf-field) を見つけたケースで内部 input を強引に focus すると、
        // MUI X DatePicker のように focus でポップアップが開いて scroll を上書きしたり、
        // 余計な UI が出る副作用があるため、ここでは focus しない (scroll に専念)。
        if (topMostTarget.matches("input,select,textarea,button,[tabindex]")) {
          topMostTarget.focus({ preventScroll: true });
        }
      }
    },
    [scope, focus],
  );
};
